import { Button } from './Button';
import { cn } from './utils';


/**
 * Shared filter-bar primitives (audit 2e7158c3). ChannelChip, the scrim
 * dropdown, the selection-count pill and the bulk button were duplicated (and
 * had drifted) across the Sales / Listings / Inventory filter bars. Promoted here
 * per styleguide §6. Each preserves the callers' rendered output; genuine
 * per-bar differences (chip hover, popover width/padding) are passed via
 * `className`.
 */


/** Single-select channel filter chip. `className` carries the per-bar extras
 * (e.g. the Listings hover override); the base + active/inactive classes are the
 * shared invariant. */
function ChannelChip({ label, active, onClick, className }) {
  return (
    <Button
      variant="secondary"
      className={cn(
        'h-auto rounded-full border-hairline-strong bg-surface-1 px-[11px] py-1.5 text-table',
        active ? 'border-primary/50 font-medium text-ink' : 'font-normal text-ink-muted',
        className,
      )}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

ChannelChip.displayName = 'ChannelChip';


/** Scrim + anchored dropdown popover: a full-viewport transparent close target
 * plus the positioned menu container. `className` sets the container's
 * width/gap/padding (default w-60 · gap-2 · p-3). Closes on outside click; the
 * caller closes on pick. */
function ScrimPopover({ open, onClose, className, children }) {
  if (!open) {
    return null;
  }
  return (
    <>
      <Button
        variant="ghost"
        aria-label="Close"
        className="fixed inset-0 z-20 h-auto cursor-default rounded-none p-0 hover:bg-transparent"
        onClick={onClose}
      />

      <div
        className={cn(
          'absolute left-0 top-full z-30 mt-1.5 flex w-60 flex-col gap-2 rounded-lg',
          'border border-hairline bg-surface-1 p-3 shadow-lg',
          className,
        )}
      >
        {children}
      </div>
    </>
  );
}

ScrimPopover.displayName = 'ScrimPopover';


/** 'N selected' pill shown at the head of a bulk action bar. */
function SelectionPill({ count }) {
  return (
    <span className="rounded-full bg-primary-soft px-2.5 py-1 text-xs font-semibold text-primary">
      {`${count} selected`}
    </span>
  );
}

SelectionPill.displayName = 'SelectionPill';


/** Bulk action bar button (mark-listed, status, delete, export…). `danger` tints
 * it rose; `disabled` dims it; `title` sets the tooltip. */
function BulkButton({ label, onClick, disabled, title, danger }) {
  return (
    <Button
      title={title}
      variant="ghost"
      disabled={disabled}
      className={cn(
        'h-auto rounded-md border border-hairline-strong bg-surface-1 px-[11px] py-[7px]',
        'text-table font-medium text-ink hover:bg-surface-1 hover:text-ink',
        danger && 'text-danger hover:text-danger',
        disabled && 'opacity-60',
      )}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

BulkButton.displayName = 'BulkButton';


export { BulkButton, ChannelChip, ScrimPopover, SelectionPill };
